import { FormStepType } from "../types";
import getYesNoOptions from "../options/yes_no_options";
import { gender } from "../options/gender";
import {
  docType,
  firstName,
  lastName,
  documentNumber,
  issuanceDate,
  nameChanged,
} from "./fields/passport-details-fields";
import { travelPlansGroup } from "./fields/travel-plans-fields";

export const passport_details_corrections: FormStepType = {
  id: 3,
  name: "passport-details",
  title: "3. Passport Details",
  groups: [
    {
      name: "issued-documents",
      fields: [docType({ form: "passport-corrections" })],
      subGroups: [
        {
          name: "passport-book",
          title: "U.S Passport Book",
          subTitle: "Your name as printed on your most recent U.S. Passport book",
          condition: {
            field: "documentType2",
            values: ["passportbook", "both"],
          },
          fields: [issuanceDate({}), firstName({}), lastName({}), documentNumber({})],
          cols: 6,
        },
        {
          name: "passport-card",
          title: "U.S Passport Card",
          subTitle: "Your name as printed on your most recent U.S. Passport card",
          condition: {
            field: "documentType2",
            values: ["passportcard", "both"],
          },
          fields: [
            issuanceDate({ type: "card" }),
            firstName({ type: "card" }),
            lastName({ type: "card" }),
            documentNumber({ type: "card" }),
          ],
          cols: 6,
        },
      ],
    },

    {
      name: "name-has-changed",
      fields: [nameChanged({ form: "passport-renewal" })],
    },
    {
      name: "nameChangeSubGroup",
      condition: {
        field: "nameChange",
        value: "yes",
      },
      fields: [
        {
          component: "TextInput",
          name: "nameChangeFirstName",
          label: "First and Middle Name",
          type: "text",
          placeholder: "",
          cols: 4,
          maxLength: 17,
          validation: {
            required: "Please enter the first name.",
            maxLength: 17,
            minLength: {
              value: 2,
              message: "The first name must have at least two characters.",
            },
          },
          defaultValueAs: "firstName",
        },
        {
          component: "TextInput",
          name: "nameChangeLastName",
          label: "Last Name",
          type: "text",
          placeholder: "",
          cols: 4,
          maxLength: 21,
          validation: {
            required: "Please enter the last name.",
            maxLength: 21,
            minLength: {
              value: 2,
              message: "The last name must have at least two characters.",
            },
          },
          defaultValueAs: "lastName",
        },
      ],
    },
    {
      name: "incorrect-printed-info",
      fields: [
        {
          component: "Radio",
          name: "incorrectPrintedInfo",
          label:
            "Were your personal details printed incorrectly in your U.S. passport book and/or card?",
          type: "radio",
          validation: {
            required: "Please select an option.",
          },
          placeholder: "Select your option",
          options: getYesNoOptions({}),
          value: "no",
        },
      ],
    },
    {
      name: "incorrectPrintedInfoSubGroup",
      subTitle: "Please select the item(s) that are printed incorrectly",
      condition: {
        field: "incorrectPrintedInfo",
        value: "yes",
      },
      fields: [
        {
          component: "Check",
          name: "firstNameIncorrectCheck",
          label: "First and Middle Name",
          type: "checkbox",
          defaultValue: false,
          validation: {},
          cols: 5,
        },
        {
          component: "TextInput",
          name: "firstNameIncorrectPrint",
          label: "",
          type: "text",
          placeholder: "First and Middle Name",
          cols: 5,
          maxLength: 17,
          validation: {
            required: "Please enter the first name.",
            maxLength: 17,
            minLength: {
              value: 2,
              message: "The first name must have at least two characters.",
            },
          },
          defaultValueAs: "firstName",
          condition: {
            field: "firstNameIncorrectCheck",
            value: true,
          },
        },

        {
          component: "Check",
          name: "lastNameIncorrectCheck",
          label: "Last Name",
          type: "checkbox",
          defaultValue: false,
          validation: {},
          cols: 5,
        },
        {
          component: "TextInput",
          name: "lastNameIncorrectPrint",
          label: "",
          type: "text",
          placeholder: "Last Name",
          cols: 5,
          maxLength: 17,
          validation: {
            required: "Please enter the last name.",
            maxLength: 17,
            minLength: {
              value: 2,
              message: "The last name must have at least two characters.",
            },
          },
          defaultValueAs: "lastName",
          condition: {
            field: "lastNameIncorrectCheck",
            value: true,
          },
        },

        {
          component: "Check",
          name: "dateOfBirthIncorrectCheck",
          label: "Date of Birth",
          type: "checkbox",
          defaultValue: false,
          validation: {},
          cols: 5,
        },
        {
          component: "Date",
          name: "dateOfBirthIncorrectPrint",
          label: "",
          type: "date",
          maxElegibleDate: "today",
          placeholder: "Date of Birth",
          cols: 5,
          validation: {
            required: "Please enter the birth date.",
          },
          defaultValueAs: "birthDate",
          condition: {
            field: "dateOfBirthIncorrectCheck",
            value: true,
          },
        },

        {
          component: "Check",
          name: "placeOfBirthIncorrectCheck",
          label: "Place of Birth",
          type: "checkbox",
          defaultValue: false,
          validation: {},
          cols: 5,
        },
        {
          component: "TextInput",
          name: "placeOfBirthIncorrectPrint",
          label: "",
          type: "text",
          placeholder: "Place of Birth",
          cols: 5,
          maxLength: 17,
          validation: {
            required: "Please enter the place of Birth.",
            maxLength: 17,
            minLength: {
              value: 2,
              message: "The place of birth must have at least two characters.",
            },
          },
          defaultValueAs: "birthCountry",
          condition: {
            field: "placeOfBirthIncorrectCheck",
            value: true,
          },
        },

        {
          component: "Check",
          name: "genderIncorrectCheck",
          label: "Gender",
          type: "checkbox",
          defaultValue: false,
          validation: {},
          cols: 5,
        },
        {
          component: "Radio",
          name: "genderIncorrectPrint",
          label: "",
          type: "radio",
          placeholder: "Gender",
          class: "radio--switch",
          cols: 5,
          validation: {
            required: "Please enter the gender.",
          },
          options: gender,
          defaultValueAs: "sex",
          condition: {
            field: "genderIncorrectCheck",
            value: true,
          },
        },
      ],
    },
    {
      name: "limited-validity-passport",
      fields: [
        {
          component: "Radio",
          name: "limitedValidityPassport",
          label: "Was your most recent U.S. passport book limited for two years or less?",
          type: "radio",
          validation: {
            required: "Please select a value.",
          },
          placeholder: "Please select a value.",
          options: getYesNoOptions({}),
          defaultValue: "No",
          tooltip:
            "Submit proof of your U.S. citizenship (e.g. birth or naturalization certificate) and/or identification (e.g. driver's license or state ID).",
        },
      ],
    },
    travelPlansGroup,
  ],
};
